import { Person } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-premium';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
  SolaForceDataGrid,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { EnumListKey, useAllEnums } from '~/data/enums';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportTypes,
  PersonnelListReportData,
  usePersonnelListReportData,
} from '~/data/reports';
import { ReportDatasetRowCount } from '~/scenes/PortalModules/Analytics/common';
import { ReportViewLayout } from '~/scenes/PortalModules/Analytics/common/ReportViewLayout';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common/useReportNavigateToView';
import {
  convertDateTypeDatePickerProps,
  convertToDateOrUndefined,
  getFormattedDateString,
} from '~/utils/helpers/date';
import { getEnumName } from '~/utils/helpers/enum';

import { ReportDatasetLayout } from '../../common/ReportDatasetLayout';

export const PersonnelListReport = () => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { getEmployeeById } = useAllEmployees();
  const { getEnums } = useAllEnums();
  const enums = useMemo(() => {
    return {
      employeePositions: getEnums(EnumListKey.EMPLOYEE_POSITION),
      countries: getEnums(EnumListKey.COUNTRY),
    };
  }, [getEnums]);

  const [startDate, setStartDate] = useState<Date>(new Date());

  const { data, isLoading } = usePersonnelListReportData({
    reportType: {
      type: AnalyticsReportTypes.Company,
    },
    params: {
      startDate: startDate,
      endDate: startDate,
      empScope: AnalyticsReportEmployeeScope.AllEmployees,
    },
  });

  const columns = useMemo((): GridColDef<PersonnelListReportData>[] => {
    const { employeePositions, countries } = enums;

    return [
      {
        field: 'fEmpId',
        headerName: t('laPicture'),
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        flex: 0.75,
      },
      {
        field: 'fEmpFirstName',
        headerName: t('laFirstName'),
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        flex: 1,
      },
      {
        field: 'fEmpLastName',
        headerName: t('laLastName'),
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.fEmpId);
              }}
            />
          );
        },
        flex: 1,
      },
      {
        field: 'empNum',
        headerName: t('laEmpId'),
        valueGetter: ({ row: { fEmpId } }) =>
          getEmployeeById(fEmpId)?.fEmpPersonNumber,
      },
      {
        field: 'fEmpUnit',
        headerName: t('laOrgUnit'),
        flex: 2.5,
      },
      {
        field: 'fEmpUnitNumber',
        headerName: t('laUnitNumber'),
        flex: 1,
      },
      {
        field: 'fEmpPosition',
        headerName: t('laPosition'),
        valueGetter: ({ value }) => getEnumName(employeePositions, value),
        flex: 2.5,
      },
      {
        field: 'fEmpStartDate',
        headerName: t('laStartDate'),
        valueGetter: ({ value }) => convertToDateOrUndefined(value),
        valueFormatter: ({ value }) => getFormattedDateString(value, 'L'),
        flex: 1,
      },
      {
        field: 'fEmpWorkCountry',
        headerName: t('laCountry'),
        valueGetter: ({ value }) => getEnumName(countries, value),
        flex: 1.5,
      },
      {
        field: 'fEmpLegalEntityName',
        headerName: t('laLegalEntity'),
        flex: 2,
      },
      {
        field: 'fEmpCostCenterName',
        headerName: t('laCostCenter'),
        flex: 2,
      },
      {
        field: 'fEmpCostCenterCode',
        headerName: t('laCostCenterCode'),
        flex: 1.5,
      },
    ];
  }, [t, enums, getEmployeeById, goToEmployeeBasicDataView]);

  return (
    <ReportViewLayout
      heading={t('laPersonelList')}
      slots={{
        toolbar: {
          main: (
            <ReportDatasetLayout>
              <DatePicker
                {...convertDateTypeDatePickerProps({
                  value: startDate,
                  onChange: (value) => {
                    setStartDate(!!value ? value : new Date());
                  },
                })}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
              <ReportDatasetRowCount
                count={data.length}
                description={t('laTotalRecords')}
                icon={<Person />}
              />
            </ReportDatasetLayout>
          ),
        },
      }}
    >
      <SolaForceDataGrid
        columns={columns}
        rows={data}
        loading={isLoading}
        density={'compact'}
        getRowId={(row) => row.fEmpId}
      />
    </ReportViewLayout>
  );
};
